import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Text } from '@clay-run/glaze/text';
import { MentionCandidatesResponseSchema, mentionsInText, mentionToken, type MentionCandidatesResponse, type NoteMention } from '@shared/flow/notes';
import { useApi } from '../../../../lib/api';
import { fetchJson } from '../../../../lib/apiFetchJson';
import { useDebouncedValue } from '../../useDebouncedValue';
import { FLOW_NOTES_MENTION_CANDIDATES_KEY } from './notes-query-keys';
import { NotesOptionRow, recordMark } from './NotesOptionRow';

/** Most mentions one note can carry (`PostNoteBodySchema`). */
export const MAX_MENTIONS = 20;
const SUGGESTION_DEBOUNCE_MS = 150;

/**
 * The active `@query` just before the caret. The `@` must start the text or follow whitespace, so an
 * email address like `a@b` never opens the picker. Once open, spaces are part of the query (so
 * "Jordan Dinopoulos" can be searched); it ends only at a newline, another `@`, Escape, a pick, or
 * when the `@` is deleted. Capped at MAX_QUERY_LENGTH so a stray `@` does not turn the rest of a
 * long note into a search.
 */
const TRIGGER = /(^|\s)@([^@\n]*)$/;
const MAX_QUERY_LENGTH = 60;

export interface MentionTrigger {
  /** Offset of the `@`. */
  start: number;
  /** The typed fragment, lowercased. */
  query: string;
}

/** `mentions` are the already-picked ones: text typed after a committed `@Name ` is not a new query. */
export function findMentionTrigger(text: string, caret: number, mentions: readonly NoteMention[] = []): MentionTrigger | null {
  const before = text.slice(0, caret);
  const match = TRIGGER.exec(before);
  if (!match || match[2].length > MAX_QUERY_LENGTH) return null;
  const start = match.index + match[1].length;
  if (mentions.some(m => text.startsWith(mentionToken(m), start))) return null;
  return { start, query: match[2].toLowerCase() };
}

/** Replaces the active fragment before `caret` with `@label ` (one trailing space); text is untouched when there is no fragment. */
export function applyMention(text: string, caret: number, label: string): string {
  const trigger = findMentionTrigger(text, caret);
  if (!trigger) return text;
  return `${text.slice(0, trigger.start)}@${label} ${text.slice(caret)}`;
}

/** Drops mentions whose `@label` is no longer in the body, and repeats of the same person. */
export function pruneMentions(body: string, mentions: NoteMention[]): NoteMention[] {
  const seen = new Set<string>();
  return mentionsInText(mentions, body).filter(m => {
    const id = `${m.kind}:${m.id}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Returns true when the picker consumed the key (the composer then skips its own handling). */
export type MentionKeyHandler = (event: KeyboardEvent<HTMLTextAreaElement>) => boolean;

export interface MentionPickerProps {
  /** The thread's account; candidates are scoped to it, so a contact without one gets no picker. */
  accountId: string | null;
  value: string;
  caret: number;
  setValue: (next: string, caret?: number) => void;
  mentions: NoteMention[];
  setMentions: (next: NoteMention[]) => void;
  registerKeyHandler: (handler: MentionKeyHandler | null) => void;
}

interface Candidate {
  kind: NoteMention['kind'];
  id: string;
  label: string;
}

/** Round avatar for a person (teammate or contact), square for an account: same marks as the pickers. */
const GROUPS = [
  { key: 'contacts', heading: 'Contacts', kindLabel: 'Contact', kind: 'contact' },
  { key: 'teammates', heading: 'Teammates', kindLabel: 'Teammate', kind: 'user' },
  { key: 'accounts', heading: 'Accounts', kindLabel: 'Account', kind: 'account' },
] as const satisfies readonly { key: keyof MentionCandidatesResponse; heading: string; kindLabel: string; kind: NoteMention['kind'] }[];

/** `query` is already trimmed: the cache key and the request carry the same string. */
function useMentionCandidates(accountId: string | null, query: string, enabled: boolean) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: [...FLOW_NOTES_MENTION_CANDIDATES_KEY, accountId, query],
    enabled: enabled && accountId !== null,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // The failure renders as a row in the picker, so no global toast.
    meta: { suppressErrorToast: true },
    queryFn: () => {
      const qs = new URLSearchParams({ accountId: accountId ?? '', q: query });
      return fetchJson(apiFetch, MentionCandidatesResponseSchema, `/api/flow/notes/mention-candidates?${qs.toString()}`);
    },
  });
}

/**
 * `@` suggestions above the note box: Contacts / Teammates / Accounts exactly
 * as the endpoint returns them (it owns eligibility), arrows + Enter select,
 * Tab also selects, Escape closes. Spaces are part of the query; only Escape, a pick, or deleting the `@` closes it.
 */
export function MentionPicker({ accountId, value, caret, setValue, mentions, setMentions, registerKeyHandler }: MentionPickerProps) {
  const trigger = findMentionTrigger(value, caret, mentions);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  if (trigger === null && dismissedAt !== null) setDismissedAt(null);

  const typed = (trigger?.query ?? '').trim();
  const debounced = useDebouncedValue(typed, SUGGESTION_DEBOUNCE_MS);
  const { data, isError, isPending, isPlaceholderData } = useMentionCandidates(accountId, debounced, trigger !== null && debounced === typed);
  // `keepPreviousData` shows the last fragment's rows while the new ones load;
  // those are dimmed and cannot be selected (they may not match what is typed).
  const stale = isPlaceholderData || debounced !== typed;

  const open = trigger !== null && trigger.start !== dismissedAt && accountId !== null;
  const atCap = mentions.length >= MAX_MENTIONS;
  const candidates = useMemo<Candidate[]>(
    () => (atCap || isError || !data ? [] : GROUPS.flatMap(g => data[g.key].map(c => ({ kind: g.kind, id: c.id, label: c.label })))),
    [atCap, isError, data],
  );
  const current = Math.min(active, Math.max(candidates.length - 1, 0));

  const select = (candidate: Candidate) => {
    if (trigger === null || atCap || stale) return;
    setValue(applyMention(value, caret, candidate.label), trigger.start + candidate.label.length + 2);
    setMentions([...mentions.filter(m => !(m.kind === candidate.kind && m.id === candidate.id)), candidate]);
  };

  useEffect(() => {
    registerKeyHandler(event => {
      if (!open) return false;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (trigger !== null) setDismissedAt(trigger.start);
        return true;
      }
      // Stale rows, or nothing back yet for the first fragment (cold cache): swallow Enter so it neither
      // picks a leftover row nor posts a half-typed @fragment. An error, the cap, or an empty result
      // leaves Enter to the composer.
      const waiting = candidates.length > 0 ? stale : !isError && !atCap && (stale || isPending);
      if (waiting && (event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
        event.preventDefault();
        return true;
      }
      if (candidates.length === 0) return false;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((event.key === 'ArrowDown' ? current + 1 : current - 1 + candidates.length) % candidates.length);
        return true;
      }
      if ((event.key === 'Tab' || event.key === 'Enter') && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        select(candidates[current]);
        return true;
      }
      return false;
    });
    return () => registerKeyHandler(null);
  });

  if (!open) return null;
  if (!isError && !atCap && candidates.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Mention suggestions"
      aria-busy={stale}
      className={clsx(
        'flex max-h-56 flex-col gap-0.5 overflow-y-auto rounded-lg border border-border-primary bg-bg-primary p-1 shadow-md',
        stale && 'opacity-60',
      )}
    >
      {atCap && (
        <Text size="xs" variant="tertiary" className="px-2 py-1.5">
          {`You can mention up to ${MAX_MENTIONS} at a time.`}
        </Text>
      )}
      {isError && (
        <Text size="xs" variant="tertiary" className="px-2 py-1.5">
          Couldn't load suggestions
        </Text>
      )}
      {GROUPS.map(group => {
        const rows = candidates.filter(c => c.kind === group.kind);
        if (rows.length === 0) return null;
        return (
          <div key={group.key} role="group" aria-label={group.heading} className="flex flex-col gap-0.5">
            <Text size="xs" variant="tertiary" className="px-2 pt-1">
              {group.heading}
            </Text>
            {rows.map(candidate => {
              const index = candidates.indexOf(candidate);
              return (
                <button
                  key={`${candidate.kind}:${candidate.id}`}
                  type="button"
                  role="option"
                  aria-selected={index === current}
                  aria-label={candidate.label}
                  // Select on click (also what keyboard / assistive-tech activation fires); mousedown
                  // only prevents the default so focus (and the caret) stays in the textarea.
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => select(candidate)}
                  onMouseEnter={() => setActive(index)}
                  className="flex min-h-8 w-full items-center gap-3 rounded-md px-2 py-1 text-left text-sm text-content-primary aria-selected:bg-bg-secondary"
                >
                  <NotesOptionRow
                    label={candidate.label}
                    mark={recordMark(candidate.kind, candidate.id)}
                    trailing={<span className="shrink-0 text-xs text-content-tertiary">{group.kindLabel}</span>}
                  />
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
